import { useState } from "react";
import { useMyProfile } from "../data";
import {
  disconnectGoogleContacts,
  startGoogleContactsConnect,
  syncGoogleContactsNow,
} from "../actions";

/**
 * Connects a Google account so its contacts flow into the Inbox.
 *
 * Each CRM user connects their own account, which is how two people — the rabbi
 * and his wife — each sync their own address book without seeing or being able
 * to revoke the other's.
 *
 * The status shown here comes from the user's own profile document, not from the
 * connection record: that record holds the refresh token and is closed to every
 * client, so the server mirrors the harmless parts across.
 *
 * The copy is careful about two things the user would otherwise have to guess:
 * that this reads and never writes, and that contacts wait in the Inbox rather
 * than appearing as people.
 */

type Status = "active" | "revoked" | "disconnected" | "error" | undefined;

const STATUS_LABEL: Record<string, { text: string; tone: string }> = {
  active: { text: "מחובר", tone: "ok" },
  revoked: { text: "ההרשאה בוטלה", tone: "bad" },
  error: { text: "תקלה בסנכרון", tone: "warn" },
  disconnected: { text: "לא מחובר", tone: "off" },
};

function formatWhen(value: Date | null): string {
  if (!value) return "עוד לא סונכרן";
  return new Intl.DateTimeFormat("he-IL", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(value);
}

export function GoogleContactsCard() {
  const profile = useMyProfile();
  const connection = profile?.googleContacts;
  const status = connection?.status as Status;

  const [busy, setBusy] = useState<null | "connect" | "sync" | "disconnect">(null);
  const [error, setError] = useState<string | null>(null);
  const [syncNote, setSyncNote] = useState<string | null>(null);
  // Shown after a connect attempt so a `redirect_uri_mismatch` can be fixed by
  // copying this exact string, rather than by guessing what the server sent.
  const [redirectUri, setRedirectUri] = useState<string | null>(null);

  const connected = status === "active";

  function readableError(err: unknown): string {
    const message = (err as { message?: string })?.message;
    return message && /[֐-׿]/.test(message) ? message : "משהו השתבש. נסו שוב.";
  }

  async function connect() {
    setBusy("connect");
    setError(null);
    try {
      const { url, redirectUri: uri } = await startGoogleContactsConnect();
      setRedirectUri(uri);
      // A new tab rather than replacing the CRM: Google's consent screen refuses
      // to render inside an iframe, and navigating away would lose the app state.
      window.open(url, "_blank", "noopener,noreferrer");
      setSyncNote("ייפתח חלון של גוגל. אחרי האישור חזרו לכאן ולחצו ״סנכרון עכשיו״.");
    } catch (err) {
      setError(readableError(err));
    } finally {
      setBusy(null);
    }
  }

  async function sync() {
    setBusy("sync");
    setError(null);
    setSyncNote(null);
    try {
      const r = await syncGoogleContactsNow();
      setSyncNote(
        r.offered > 0
          ? `${r.offered} אנשי קשר חדשים מחכים באינבוקס למיון.`
          : `אין אנשי קשר חדשים. ${r.alreadyKnown} כבר היו במערכת.`,
      );
    } catch (err) {
      setError(readableError(err));
    } finally {
      setBusy(null);
    }
  }

  async function disconnect() {
    setBusy("disconnect");
    setError(null);
    setSyncNote(null);
    try {
      await disconnectGoogleContacts();
    } catch (err) {
      setError(readableError(err));
    } finally {
      setBusy(null);
    }
  }

  const label = STATUS_LABEL[status ?? "disconnected"] ?? STATUS_LABEL.disconnected!;

  return (
    <section className="section">
      <div className="card">
        <div className="card-title">
          אנשי קשר מגוגל
          <span className={`conn-pill conn-${label.tone}`}>{label.text}</span>
        </div>

        <div className="card-pad">
          <p className="muted">
            פעמיים ביום אנחנו מושכים את אנשי הקשר מהחשבון שלכם. החדשים מחכים באינבוקס למיון.{" "}
            <strong>הם לא נכנסים לבד לאנשי הקשר</strong>, כדי שספקים ומספרים ישנים לא יתערבבו
            בקהילה.
          </p>
          <p className="faint" style={{ marginTop: 8 }}>
            קריאה בלבד: אנחנו לא משנים ולא מוחקים כלום בגוגל. מי שכבר נמצא במערכת לא יופיע שוב.
          </p>

          {connected && (
            <div className="conn-meta">
              <div>
                <span className="faint">סנכרון אחרון</span>
                <strong>{formatWhen(connection?.lastSyncAt ?? null)}</strong>
              </div>
              {connection?.lastSyncCounts && (
                <div>
                  <span className="faint">בסריקה האחרונה</span>
                  <strong>
                    {connection.lastSyncCounts.offered} חדשים · {connection.lastSyncCounts.alreadyKnown}{" "}
                    מוכרים
                  </strong>
                </div>
              )}
            </div>
          )}

          {connection?.lastSyncError && <p className="form-error">{connection.lastSyncError}</p>}
          {error && <p className="form-error">{error}</p>}
          {syncNote && <p className="conn-note">{syncNote}</p>}

          {redirectUri && !connected && (
            <div className="conn-uri">
              <p className="faint">
                אם גוגל הציגה <strong>redirect_uri_mismatch</strong>, זו הכתובת המדויקת שצריכה
                להופיע ב-<span dir="ltr">Authorized redirect URIs</span>:
              </p>
              <code dir="ltr">{redirectUri}</code>
            </div>
          )}

          <div className="btn-row" style={{ marginTop: 12 }}>
            {connected ? (
              <>
                <button className="btn" onClick={disconnect} disabled={busy !== null}>
                  {busy === "disconnect" ? "מנתקים…" : "ניתוק"}
                </button>
                <button className="btn btn-primary" onClick={sync} disabled={busy !== null}>
                  {busy === "sync" ? "מסנכרנים…" : "סנכרון עכשיו"}
                </button>
              </>
            ) : (
              <button
                className="btn btn-primary btn-wide"
                onClick={connect}
                disabled={busy !== null}
              >
                {busy === "connect" ? "פותחים…" : "חיבור חשבון גוגל"}
              </button>
            )}
          </div>

          {!connected && (
            <p className="faint" style={{ marginTop: 10 }}>
              כל אחד מחבר את החשבון שלו. כך גם שירה מחברת את אנשי הקשר שלה, בנפרד.
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
